import { useIntl } from 'react-intl';

import type { DeckSummary } from '../lib/decks.ts';
import { deckPath } from '../lib/decks.ts';
import type { DeckScope } from '../../shared/deck.ts';
import type { DeckHierarchy } from '../lib/deck-page.ts';
import { cn } from '../lib/utils.ts';
import { Alert, AlertDescription } from './ui/alert.tsx';
import { Button } from './ui/button.tsx';

type BreadcrumbEntry = {
  kind: 'section' | 'topic' | 'unit';
  name: string;
  slug: string;
};

const breadcrumbRowClasses: Record<BreadcrumbEntry['kind'], string> = {
  section: 'ps-4',
  topic: 'ps-12',
  unit: 'ps-8',
};

const contourSurfaceClasses = [
  'absolute start-[12px] top-0 end-0 h-[28px] bg-background',
  'absolute start-[28px] top-[28px] end-0 h-[28px] bg-background',
  'absolute start-[44px] top-[56px] end-0 h-[28px] bg-background',
  'absolute start-[60px] top-[84px] end-0 h-[28px] bg-background',
];

const contourExpandingSurfaceClasses = [
  'absolute start-[12px] top-0 end-0 bottom-0 bg-background',
  'absolute start-[28px] top-[28px] end-0 bottom-0 bg-background',
  'absolute start-[44px] top-[56px] end-0 bottom-0 bg-background',
  'absolute start-[60px] top-[84px] end-0 bottom-0 bg-background',
];

const contourStraightClasses = [
  'absolute start-[12px] top-[6px] h-[16px] border-s border-border',
  'absolute start-[28px] top-[33px] h-[17px] border-s border-border',
  'absolute start-[44px] top-[61px] h-[17px] border-s border-border',
  'absolute start-[60px] top-[89px] h-[17px] border-s border-border',
];

const contourExpandingStraightClasses = [
  'absolute start-[12px] top-[6px] bottom-[6px] border-s border-border',
  'absolute start-[28px] top-[33px] bottom-[6px] border-s border-border',
  'absolute start-[44px] top-[61px] bottom-[6px] border-s border-border',
  'absolute start-[60px] top-[89px] bottom-[6px] border-s border-border',
];

const contourTopCornerClass =
  'absolute start-[12px] top-0 size-[6px] bg-[radial-gradient(circle_6px_at_100%_100%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]';

const contourTransitionClasses = [
  [
    'absolute start-[12px] top-[22px] size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
    'absolute start-[17px] top-[27px] h-px w-[6px] border-t border-border',
    'absolute start-[23px] top-[27px] size-[6px] border-t border-e border-border rounded-se-[6px]',
  ],
  [
    'absolute start-[28px] top-[50px] size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
    'absolute start-[33px] top-[55px] h-px w-[6px] border-t border-border',
    'absolute start-[39px] top-[55px] size-[6px] border-t border-e border-border rounded-se-[6px]',
  ],
  [
    'absolute start-[44px] top-[78px] size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
    'absolute start-[49px] top-[83px] h-px w-[6px] border-t border-border',
    'absolute start-[55px] top-[83px] size-[6px] border-t border-e border-border rounded-se-[6px]',
  ],
];

const contourExpandingBottomCornerClasses = [
  'absolute start-[12px] bottom-0 size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
  'absolute start-[28px] bottom-0 size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
  'absolute start-[44px] bottom-0 size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
  'absolute start-[60px] bottom-0 size-[6px] bg-[radial-gradient(circle_6px_at_100%_0%,var(--background)_0_calc(6px_-_1px),var(--border)_calc(6px_-_1px)_6px,var(--chrome-background)_0)]',
];

const contourExpandingBottomClasses = [
  'absolute start-[18px] bottom-0 end-3 h-px border-b border-border',
  'absolute start-[34px] bottom-0 end-3 h-px border-b border-border',
  'absolute start-[50px] bottom-0 end-3 h-px border-b border-border',
  'absolute start-[66px] bottom-0 end-3 h-px border-b border-border',
];

function ScopedTabStaircase({ levels }: { levels: number }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 max-[760px]:hidden">
      {contourSurfaceClasses.slice(0, levels + 1).map((className, index) => (
        <span
          className={
            index === levels ? contourExpandingSurfaceClasses[index] : className
          }
          key={`surface-${index}`}
        />
      ))}
      {contourStraightClasses.slice(0, levels + 1).map((className, index) => (
        <span
          className={
            index === levels
              ? contourExpandingStraightClasses[index]
              : className
          }
          key={`straight-${index}`}
        />
      ))}
      <span className={contourTopCornerClass} />
      <span className="absolute start-[18px] top-0 end-3 h-px border-t border-border" />
      {contourTransitionClasses.slice(0, levels).map((pieces, index) => (
        <span className="contents" key={`transition-${index}`}>
          {pieces.map((className, pieceIndex) => (
            <span
              className={className}
              key={`transition-${index}-${pieceIndex}`}
            />
          ))}
        </span>
      ))}
      <span className={contourExpandingBottomCornerClasses[levels]} />
      <span className={contourExpandingBottomClasses[levels]} />
    </div>
  );
}

function BreadcrumbRow({
  current,
  entry,
  href,
}: {
  current: boolean;
  entry: BreadcrumbEntry;
  href: string;
}) {
  const className = cn(
    'relative block py-0.5',
    breadcrumbRowClasses[entry.kind],
  );
  return current ? (
    <span className={className}>{entry.name}</span>
  ) : (
    <a className={className} href={href}>
      {entry.name}
    </a>
  );
}

export function DeckNavigation({
  decks,
  currentDeckSlug,
  currentDeckScope = {},
  currentDeckHierarchy,
  error,
  isLoading,
  onRetry,
}: {
  decks: DeckSummary[] | undefined;
  currentDeckSlug: string | null;
  currentDeckScope?: DeckScope;
  currentDeckHierarchy?: DeckHierarchy | null;
  error: Error | null;
  isLoading: boolean;
  onRetry: () => void;
}) {
  const intl = useIntl();
  const scopeEntries = [
    currentDeckScope.sectionSlug
      ? { kind: 'section' as const, slug: currentDeckScope.sectionSlug }
      : null,
    currentDeckScope.unitSlug
      ? { kind: 'unit' as const, slug: currentDeckScope.unitSlug }
      : null,
    currentDeckScope.topicSlug
      ? { kind: 'topic' as const, slug: currentDeckScope.topicSlug }
      : null,
  ].filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  const resolvedScopeEntries = (() => {
    if (!currentDeckHierarchy || !currentDeckScope.sectionSlug) return null;
    const section = currentDeckHierarchy.sections.find(
      (item) => item.slug === currentDeckScope.sectionSlug,
    );
    if (!section) return null;
    const entries: BreadcrumbEntry[] = [
      { kind: 'section', name: section.name, slug: section.slug },
    ];
    if (!currentDeckScope.unitSlug) return entries;
    const unit = currentDeckHierarchy.units.find(
      (item) =>
        item.sectionId === section.id &&
        item.slug === currentDeckScope.unitSlug,
    );
    if (!unit) return null;
    entries.push({ kind: 'unit', name: unit.name, slug: unit.slug });
    if (!currentDeckScope.topicSlug) return entries;
    const topic = currentDeckHierarchy.topics.find(
      (item) =>
        item.unitId === unit.id && item.slug === currentDeckScope.topicSlug,
    );
    if (!topic) return null;
    entries.push({ kind: 'topic', name: topic.name, slug: topic.slug });
    return entries;
  })();
  return (
    <nav className="grid min-h-0">
      {isLoading ? (
        <p className="m-0 py-2 pe-4 ps-[var(--chrome-inset)] leading-relaxed text-muted-foreground">
          {intl.formatMessage({ id: 'web.decks.loading' })}
        </p>
      ) : error ? (
        <Alert
          className="mx-0 my-0 border-0 bg-transparent py-2 pe-4 ps-[var(--chrome-inset)] text-destructive"
          variant="destructive"
        >
          <AlertDescription className="grid gap-2">
            <span>{intl.formatMessage({ id: 'web.decks.error' })}</span>
            <Button
              className="min-h-10 px-3 text-sm"
              variant="secondary"
              onClick={onRetry}
              type="button"
            >
              {intl.formatMessage({ id: 'web.deckHistory.retry' })}
            </Button>
          </AlertDescription>
        </Alert>
      ) : decks?.length ? (
        <ul className="m-0 me-[-1px] min-h-0 list-none overflow-y-auto py-3 pe-0 ps-[var(--chrome-inset)] max-[760px]:me-0 max-[760px]:flex max-[760px]:max-w-full max-[760px]:gap-2 max-[760px]:overflow-x-auto max-[760px]:overflow-y-hidden max-[760px]:py-1 max-[760px]:pb-3">
          {decks.map((deck) => {
            const isCurrent = deck.slug === currentDeckSlug;
            const hasStaircase = Boolean(
              isCurrent && (!scopeEntries.length || resolvedScopeEntries),
            );
            return (
              <li
                className={cn(
                  'relative my-[0.15rem] block min-w-0 overflow-wrap-anywhere py-[0.7rem] pe-4 leading-[1.35] [&>a]:block [&>span]:block max-[760px]:my-0 max-[760px]:flex-[0_0_min(18rem,75vw)] max-[760px]:rounded-[var(--radius)] max-[760px]:border max-[760px]:border-border max-[760px]:p-[0.65rem_0.8rem]',
                  isCurrent &&
                    cn(
                      'z-[1] -ms-[var(--chrome-inset)] me-0 border-border text-foreground before:absolute before:end-0 before:z-[2] before:h-3 before:w-3 before:pointer-events-none before:bg-[radial-gradient(circle_0.75rem_at_0_0%,var(--chrome-background)_0_calc(0.75rem_-_1px),var(--border)_calc(0.75rem_-_1px)_0.75rem,var(--background)_0.75rem)] after:absolute after:end-0 after:z-[2] after:h-3 after:w-3 after:pointer-events-none after:bg-[radial-gradient(circle_0.75rem_at_0_100%,var(--chrome-background)_0_calc(0.75rem_-_1px),var(--border)_calc(0.75rem_-_1px)_0.75rem,var(--background)_0.75rem)] max-[760px]:ms-0 max-[760px]:me-0 max-[760px]:z-auto max-[760px]:rounded-[var(--radius)] max-[760px]:border max-[760px]:border-border max-[760px]:p-[0.65rem_0.8rem] max-[760px]:ps-[0.8rem] max-[760px]:before:hidden max-[760px]:after:hidden',
                      hasStaircase
                        ? 'before:-top-[11px] after:-bottom-[11px] rounded-s-none border-y-0 border-s-0 bg-[var(--chrome-background)] py-0 ps-[var(--chrome-inset)] max-[760px]:rounded-s-[var(--radius)] max-[760px]:bg-background'
                        : 'before:-top-3 after:-bottom-3 rounded-s-[var(--radius)] border-y border-s bg-background ps-[calc(var(--chrome-inset)-1px)]',
                    ),
                )}
                key={deck.id}
              >
                {isCurrent && hasStaircase ? (
                  <>
                    <ScopedTabStaircase
                      levels={resolvedScopeEntries?.length ?? 0}
                    />
                    <div className="relative grid gap-0 leading-6">
                      {resolvedScopeEntries ? (
                        <>
                          <a
                            className="block py-0.5"
                            href={deckPath(deck.slug)}
                          >
                            {deck.title}
                          </a>
                          {resolvedScopeEntries.map((entry, index) => {
                            const entryScope: DeckScope = {
                              sectionSlug: currentDeckScope.sectionSlug,
                              ...(index >= 1
                                ? { unitSlug: currentDeckScope.unitSlug }
                                : {}),
                              ...(index >= 2
                                ? { topicSlug: currentDeckScope.topicSlug }
                                : {}),
                            };
                            return (
                              <BreadcrumbRow
                                current={index === scopeEntries.length - 1}
                                entry={entry}
                                href={deckPath(deck.slug, entryScope)}
                                key={entry.kind}
                              />
                            );
                          })}
                        </>
                      ) : (
                        <span className="block py-0.5">{deck.title}</span>
                      )}
                    </div>
                  </>
                ) : isCurrent ? (
                  scopeEntries.length ? (
                    <a href={deckPath(deck.slug)}>{deck.title}</a>
                  ) : (
                    <span>{deck.title}</span>
                  )
                ) : (
                  <a href={deckPath(deck.slug)}>{deck.title}</a>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="m-0 py-2 pe-4 ps-[var(--chrome-inset)] leading-relaxed text-muted-foreground">
          {intl.formatMessage({ id: 'web.decks.empty' })}
        </p>
      )}
    </nav>
  );
}
